import type { CSSProperties } from 'react';

/**
 * The prototype's style helpers, kept as CSS custom-property references so a
 * CMS theme change repaints without a re-render. Values that were literal hex
 * in the prototype now read from the token layer.
 */

export const accent = 'var(--color-accent)';
export const accent300 = 'var(--color-accent-300)';
export const divider = 'var(--color-divider)';
export const text = 'var(--color-text)';
export const surface = 'var(--color-surface)';

/**
 * Text at a given opacity — used constantly for muted copy.
 *
 * Resolves through the theme's text colour rather than a Nocturne literal, so
 * a light variant gets dark muted text instead of invisible pale text.
 */
export function t(a: number): string {
  return `rgba(var(--color-text-rgb), ${a})`;
}

/** Accent tint fill — the "accent as a glow, never a flood" rule. */
export function accentTint(a: number): string {
  return `color-mix(in srgb, var(--color-accent) ${Math.round(a * 100)}%, transparent)`;
}

export const font = {
  h: (size: number, lh: number | string = 1.2, weight = 500): CSSProperties => ({
    font: `${weight} ${size}px/${lh} var(--font-body)`,
  }),
  mono: (size: number): CSSProperties => ({
    font: `400 ${size}px/1 var(--font-mono)`,
  }),
};

/** Uppercase eyebrow — the kicker above every screen title and section. */
export function kicker(size = 10, color = t(0.45), spacing = 0.18): CSSProperties {
  return {
    font: `500 ${size}px/1 var(--font-body)`,
    letterSpacing: `${spacing}em`,
    textTransform: 'uppercase',
    color,
  };
}

export function pill(on: boolean): CSSProperties {
  return {
    padding: '7px 13px',
    borderRadius: 20,
    font: '500 12px/1 var(--font-body)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flex: 'none',
    border: `1px solid ${on ? accent : divider}`,
    background: on ? accentTint(0.16) : 'transparent',
    color: on ? accent300 : t(0.65),
  };
}

export function chip(on: boolean): CSSProperties {
  return {
    padding: '8px 14px',
    borderRadius: 20,
    font: '500 12.5px/1 var(--font-body)',
    cursor: 'pointer',
    border: `1px solid ${on ? accent : divider}`,
    background: on ? accentTint(0.18) : 'transparent',
    color: on ? accent300 : t(0.7),
  };
}

export function segOpt(on: boolean): CSSProperties {
  return {
    padding: '8px 15px',
    font: '500 12.5px/1 var(--font-body)',
    cursor: 'pointer',
    border: 0,
    background: on ? accentTint(0.18) : 'transparent',
    color: on ? accent300 : t(0.6),
  };
}

export function dayStyle(on: boolean): CSSProperties {
  return {
    flex: 1,
    padding: '10px 0 11px',
    borderRadius: 10,
    cursor: 'pointer',
    textAlign: 'center',
    border: `1px solid ${on ? accent : divider}`,
    background: on ? accentTint(0.16) : 'transparent',
    color: on ? text : t(0.55),
  };
}

export function saveStyle(on: boolean): CSSProperties {
  return {
    flex: 'none',
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: 'pointer',
    font: '400 14px/1 var(--font-body)',
    display: 'grid',
    placeItems: 'center',
    border: `1px solid ${on ? accent : divider}`,
    background: on ? accentTint(0.2) : 'transparent',
    color: on ? accent300 : t(0.45),
  };
}

export function matchStyle(on: boolean): CSSProperties {
  if (!on) return { display: 'none' };
  return {
    display: 'inline-flex',
    alignItems: 'center',
    font: '500 9.5px/1 var(--font-body)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    padding: '4px 8px',
    borderRadius: 5,
    background: 'var(--color-accent-800)',
    color: 'var(--color-accent-300)',
  };
}

export function badgeStyle(on: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    font: '500 9.5px/1 var(--font-body)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    padding: '4px 8px',
    borderRadius: 5,
    background: on ? 'var(--color-accent-800)' : 'var(--color-neutral-800)',
    color: on ? 'var(--color-accent-300)' : 'var(--color-neutral-300)',
  };
}

export function progressDot(active: boolean): CSSProperties {
  return {
    height: 2,
    flex: 1,
    borderRadius: 2,
    background: active ? accent : 'rgba(var(--color-text-rgb), 0.15)',
  };
}

/** Outlined button — the system never fills a primary action. */
export function btn(variant: 'primary' | 'secondary' | 'ghost', extra: CSSProperties = {}): CSSProperties {
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    cursor: 'pointer',
    textDecoration: 'none',
    font: '500 14px/1.2 var(--font-body)',
    color: text,
    background: 'transparent',
    border: '1px solid transparent',
    padding: 'var(--space-2) calc(var(--space-3) * 1.2)',
    borderRadius: 'var(--radius-md)',
  };
  if (variant === 'primary') return { ...base, color: accent, borderColor: accent, ...extra };
  if (variant === 'secondary') return { ...base, borderColor: divider, ...extra };
  return { ...base, color: accent, paddingInline: 'var(--space-1)', ...extra };
}

export const cardSurface: CSSProperties = {
  borderRadius: 10,
  background: surface,
  boxShadow: 'var(--shadow-sm)',
};

export const emptyBox: CSSProperties = {
  padding: 15,
  borderRadius: 10,
  border: `1px dashed ${divider}`,
  fontSize: 12.5,
  lineHeight: 1.55,
  color: t(0.45),
};

/**
 * Resolves a bundled asset path against the deployment base.
 *
 * Bare relative paths like `assets/talent/dara.png` resolve against the
 * *document* URL, which is fine at a domain root but wrong the moment the app
 * is served from a subpath — GitHub Pages project sites, or any host that
 * mounts it under a folder. Vite exposes the configured base at runtime, so
 * prefix with it. Absolute URLs from a live CMS pass through untouched.
 */
export function assetUrl(path: string): string {
  if (/^(https?:)?\/\//.test(path) || path.startsWith('data:')) return path;
  const base = import.meta.env.BASE_URL || '/';
  return `${base}${path.replace(/^\//, '')}`;
}

/** Portrait treatment: slight desaturation so photos sit into the dark ground. */
export function portrait(url: string, posY: number): CSSProperties {
  return {
    width: '100%',
    height: '100%',
    backgroundImage: `url(${assetUrl(url)})`,
    backgroundSize: 'cover',
    backgroundPosition: `50% ${posY}%`,
    filter: 'saturate(0.88) brightness(0.94)',
    transform: 'scale(1.09)',
    transformOrigin: `50% ${posY}%`,
  };
}

export const riseIn: CSSProperties = { animation: 'rise .3s ease both' };

/**
 * Staggered entrance for a section or list item.
 *
 * Delays are capped: past about the sixth item the wait is longer than the
 * animation and the screen starts to feel slow rather than considered.
 */
export function settle(index = 0, distance: 'sm' | 'md' = 'md'): CSSProperties {
  const delay = Math.min(index, 6) * 42;
  const name = distance === 'sm' ? 'settle' : 'settleScale';
  return { animation: `${name} .42s cubic-bezier(.2,.8,.2,1) ${delay}ms both` };
}
